<template>
  <div class="app-container" v-if="isAuthenticated">
    <AdminMenu :isMobileMenuOpen="isMobileMenuOpen" />
    <div class="main-content">
      <header class="app-header d-md-none">
        <button class="mobile-menu-btn" @click="isMobileMenuOpen = !isMobileMenuOpen">
          <i class="bi bi-list"></i>
        </button>
        <span class="header-title">NeuroMancer</span>
      </header>
      <main class="page-content">
        <router-view :key="$route.params.id" />
      </main>
    </div>
  </div>
  <div v-else>
    <router-view />
  </div>
  <ErrorModal ref="errorModal" />
</template>

<script>
import { mapGetters } from 'vuex';
import AdminMenu from '@/components/AdminMenu.vue';
import ErrorModal from '@/components/ErrorModal.vue';

export default {
  name: 'App',
  components: { AdminMenu, ErrorModal },
  data() {
    return { isMobileMenuOpen: false };
  },
  computed: {
    ...mapGetters(['isAuthenticated']),
  },
  watch: {
    '$route'() { this.isMobileMenuOpen = false; },
  },
};
</script>

<style>
/* ─── Google Fonts ─────────────────────────────────── */
@import url('https://fonts.googleapis.com/css2?family=DM+Sans:ital,opsz,wght@0,9..40,300;0,9..40,400;0,9..40,500;0,9..40,600;0,9..40,700;1,9..40,400&display=swap');

/* ─── Custom badge utilities (bg-purple is not a Bootstrap 5 class) ── */
.bg-purple { background-color: #6f42c1; color: #fff; }

/* ─── Design tokens ─────────────────────────────────── */
:root {
  /* Brand */
  --nc-primary:        #06b6d4;
  --nc-primary-dark:   #0891b2;
  --nc-primary-glow:   rgba(6, 212, 212, 0.25);
  --nc-primary-subtle: rgba(6, 182, 212, 0.10);

  /* Surfaces */
  --nc-bg:             #020617;
  --nc-bg-2:           #050d1f;
  --nc-bg-3:           #0a1628;
  --nc-card:           #080f22;
  --nc-card-border:    rgba(255, 255, 255, 0.07);
  --nc-card-hover:     #0c1530;

  /* Override Bootstrap 5 CSS variables * !important;
  --bs-dark-rgb:             #fff !important;
  --bs-body-bg:              #020617 !important;
  --bs-body-color:           #e2e8f0 !important;
  --bs-secondary-bg:         #080f22 !important;
  --bs-tertiary-bg:          #0a1628 !important;
  --bs-border-color:         rgba(255,255,255,0.07) !important;
  --bs-border-color-translucent: rgba(255,255,255,0.07) !important;
  --bs-emphasis-color:       #f1f5f9 !important;
  --bs-secondary-color:      #64748b !important;
  --bs-link-color:           #06b6d4 !important;
  --bs-link-hover-color:     #0891b2 !important;
  --bs-card-bg:              #080f22 !important;
  --bs-card-border-color:    rgba(255,255,255,0.07) !important;
  --bs-card-cap-bg:          rgba(255,255,255,0.03) !important;
  --bs-table-bg:             transparent !important;
  --bs-table-striped-bg:     rgba(255,255,255,0.02) !important;
  --bs-table-hover-bg:       rgba(255,255,255,0.03) !important;
  --bs-table-border-color:   rgba(255,255,255,0.05) !important;
  --bs-input-bg:             rgba(255,255,255,0.04) !important;
  --bs-input-border-color:   rgba(255,255,255,0.10) !important;
  --bs-input-color:          #e2e8f0 !important;
  --bs-input-placeholder-color: #64748b !important;
  --bs-modal-bg:             #050d1f !important;
  --bs-modal-border-color:   rgba(255,255,255,0.07) !important;
  --bs-dropdown-bg:          #0a1628 !important;
  --bs-dropdown-border-color: rgba(255,255,255,0.07) !important;
  --bs-dropdown-link-color:  #e2e8f0 !important;
  --bs-dropdown-link-hover-bg: rgba(255,255,255,0.06) !important;
  --bs-pagination-bg:        rgba(255,255,255,0.04) !important;
  --bs-pagination-border-color: rgba(255,255,255,0.10) !important;
  --bs-pagination-color:     #64748b !important;
  --bs-pagination-active-bg: #06b6d4 !important;
  --bs-pagination-active-border-color: #06b6d4 !important;
  --bs-list-group-bg:        transparent !important;
  --bs-list-group-border-color: rgba(255,255,255,0.07) !important;

  /* Sidebar */
  --nc-sidebar-width:  196px;
  --nc-sidebar-bg:     linear-gradient(180deg,
                         color-mix(in srgb, #020617 72%, #06b6d4 28%) 0%,
                         #020617 60%);
  --nc-sidebar-border: rgba(6, 182, 212, 0.10);

  /* Text */
  --nc-text:           #e2e8f0 !important;
  --nc-text-muted:     #64748b !important;
  --nc-text-subtle:    #334155 !important;

  /* Status */
  --nc-success:        #10b981 !important;
  --nc-warning:        #f59e0b !important;
  --nc-danger:         #ef4444 !important;
  --nc-info:           #3b82f6 !important;

  /* Geometry */
  --nc-radius:         4px !important;
  --nc-radius-sm:      3px !important;
  --nc-radius-lg:      8px !important;
  --nc-gap:            12px !important;
  --nc-pad:            16px !important;

  /* Effects */
  --nc-shadow-sm:      0 1px 4px rgba(0,0,0,.5), 0 0 1px rgba(0,0,0,.4) !important;
  --nc-shadow:         0 4px 16px rgba(0,0,0,.6), 0 1px 4px rgba(0,0,0,.4) !important;
  --nc-glass-blur:     blur(12px) !important;
  --nc-transition:     150ms cubic-bezier(0.4, 0, 0.2, 1) !important;
}

/* ─── Reset & Base ──────────────────────────────────── */
*, *::before, *::after { box-sizing: border-box; }

html {
  scroll-behavior: smooth !important;
  background-color: #020617 !important;
}

body {
  margin: 0 !important;
  font-family: 'DM Sans', system-ui, -apple-system, sans-serif !important;
  font-size: 14px !important;
  line-height: 1.55 !important;
  background-color: #020617 !important;
  color: #e2e8f0 !important;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
}

/* Kill any Bootstrap white backgrounds at root level */
#app, .app-container, .main-content, .page-content {
  background-color: #020617 !important;
  color: #e2e8f0 !important;
}

/* ─── Scrollbars ────────────────────────────────────── */
::-webkit-scrollbar          { width: 5px; height: 5px; }
::-webkit-scrollbar-track    { background: transparent; }
::-webkit-scrollbar-thumb    { background: rgba(255,255,255,.12); border-radius: 10px; }
::-webkit-scrollbar-thumb:hover { background: rgba(255,255,255,.22); }

/* ─── Layout ────────────────────────────────────────── */
.app-container {
  display: flex;
  min-height: 100vh;
}
.main-content {
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow-x: hidden;
  min-width: 0;
}
.page-content {
  flex: 1;
  padding: var(--nc-pad);
}

/* ─── Mobile header ─────────────────────────────────── */
.app-header {
  background: rgba(2, 6, 23, 0.9);
  backdrop-filter: var(--nc-glass-blur);
  border-bottom: 1px solid var(--nc-card-border);
  padding: 0.5rem 1rem;
  display: flex;
  align-items: center;
  gap: 0.75rem;
  position: sticky;
  top: 0;
  z-index: 100;
}
.mobile-menu-btn {
  background: none;
  border: none;
  color: var(--nc-text);
  font-size: 20px;
  cursor: pointer;
  padding: 4px;
  line-height: 1;
}
.header-title {
  font-size: 15px;
  font-weight: 600;
  color: var(--nc-text);
  letter-spacing: -0.3px;
}

/* ═══════════════════════════════════════════════════════
   BOOTSTRAP DARK OVERRIDES
═══════════════════════════════════════════════════════ */

/* ─── Cards ─────────────────────────────────────────── */
.card {
  background-color: #080f22 !important;
  border: 1px solid rgba(255,255,255,0.07) !important;
  border-radius: 4px !important;
  box-shadow: 0 1px 4px rgba(0,0,0,.5), 0 0 1px rgba(0,0,0,.4) !important;
  color: #e2e8f0 !important;
}
.card:hover { border-color: rgba(6,182,212,0.2) !important; }
.card-header {
  background-color: rgba(255,255,255,.025) !important;
  border-bottom: 1px solid rgba(255,255,255,0.07) !important;
  color: #e2e8f0 !important;
  font-weight: 500;
}
.card-body  { color: #e2e8f0 !important; background-color: transparent !important; }
.card-footer {
  background-color: rgba(255,255,255,.02) !important;
  border-top: 1px solid rgba(255,255,255,0.07) !important;
}

/* ─── Tables ─────────────────────────────────────────── */
.table {
  color: var(--nc-text) !important;
  --bs-table-bg: transparent !important;
  --bs-table-hover-bg: rgba(255,255,255,.03);
  --bs-table-striped-bg: rgba(255,255,255,.02);
  --bs-table-border-color: rgba(255,255,255,.05);
}
.table > thead > tr > th {
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: .06em;
  color: var(--nc-text-muted) !important;
  border-bottom-color: rgba(255,255,255,.07) !important;
  padding: 8px 12px;
  white-space: nowrap;
}
.table > tbody > tr > td {
  border-color: rgba(255,255,255,.04) !important;
  padding: 10px 12px;
  vertical-align: middle;
  color: var(--nc-text-muted);
}
.table > tbody > tr:last-child > td { border-bottom: none !important; }
.table-hover > tbody > tr:hover > td { background: rgba(255,255,255,.03) !important; }

/* ─── Forms ──────────────────────────────────────────── */
.form-control,
.form-select {
  background: rgba(255,255,255,.04) !important;
  border: 1px solid rgba(255,255,255,.1) !important;
  border-radius: var(--nc-radius) !important;
  color: var(--nc-text) !important;
  font-family: inherit;
  transition: border-color var(--nc-transition), box-shadow var(--nc-transition);
}
.form-control:focus,
.form-select:focus {
  background: rgba(255,255,255,.06) !important;
  border-color: var(--nc-primary) !important;
  box-shadow: 0 0 0 3px var(--nc-primary-glow) !important;
  color: var(--nc-text) !important;
  outline: none;
}
.form-control::placeholder { color: var(--nc-text-muted) !important; opacity: .7; }
.form-control:disabled,
.form-select:disabled {
  background: rgba(255,255,255,.02) !important;
  opacity: .5;
}
/* Select dropdown options */
.form-select option {
  background: var(--nc-bg-3) !important;
  color: var(--nc-text) !important;
}
.form-label { color: var(--nc-text) !important; font-size: 13px; font-weight: 500; margin-bottom: 6px; }
.form-text  { color: var(--nc-text-muted) !important; }
.form-check-input {
  background-color: rgba(255,255,255,.06) !important;
  border-color: rgba(255,255,255,.2) !important;
}
.form-check-input:checked {
  background-color: var(--nc-primary) !important;
  border-color: var(--nc-primary) !important;
}
.form-check-input:focus {
  box-shadow: 0 0 0 3px var(--nc-primary-glow) !important;
}

/* Input group */
.input-group-text {
  background: rgba(255,255,255,.06) !important;
  border: 1px solid rgba(255,255,255,.1) !important;
  color: var(--nc-text-muted) !important;
  border-radius: var(--nc-radius) !important;
}

/* ─── Buttons ────────────────────────────────────────── */
.btn {
  font-family: inherit;
  font-weight: 500;
  border-radius: var(--nc-radius) !important;
  transition: all var(--nc-transition) !important;
  font-size: 13px;
}
.btn-primary {
  background: var(--nc-primary) !important;
  border-color: var(--nc-primary) !important;
  color: #020617 !important;
  font-weight: 600;
  box-shadow: 0 0 14px var(--nc-primary-glow);
}
.btn-primary:hover,
.btn-primary:focus {
  background: var(--nc-primary-dark) !important;
  border-color: var(--nc-primary-dark) !important;
  box-shadow: 0 0 20px var(--nc-primary-glow) !important;
  transform: translateY(-1px);
}
.btn-primary:active { transform: translateY(0) !important; }

.btn-outline-primary {
  border-color: var(--nc-primary) !important;
  color: var(--nc-primary) !important;
  background: transparent !important;
}
.btn-outline-primary:hover {
  background: var(--nc-primary-subtle) !important;
  box-shadow: 0 0 12px var(--nc-primary-glow);
}

.btn-secondary {
  background: rgba(255,255,255,.08) !important;
  border-color: rgba(255,255,255,.12) !important;
  color: var(--nc-text) !important;
}
.btn-secondary:hover {
  background: rgba(255,255,255,.12) !important;
  border-color: rgba(255,255,255,.18) !important;
  color: var(--nc-text) !important;
}

.btn-outline-secondary {
  border-color: rgba(255,255,255,.12) !important;
  color: var(--nc-text-muted) !important;
  background: transparent !important;
}
.btn-outline-secondary:hover {
  background: rgba(255,255,255,.06) !important;
  color: var(--nc-text) !important;
  border-color: rgba(255,255,255,.2) !important;
}

.btn-success { background: var(--nc-success) !important; border-color: var(--nc-success) !important; color: #fff !important; }
.btn-danger  { background: var(--nc-danger)  !important; border-color: var(--nc-danger)  !important; color: #fff !important; }
.btn-warning { background: var(--nc-warning) !important; border-color: var(--nc-warning) !important; color: #020617 !important; }

.btn-outline-success { border-color: var(--nc-success) !important; color: var(--nc-success) !important; }
.btn-outline-success:hover { background: rgba(16,185,129,.1) !important; }
.btn-outline-danger  { border-color: var(--nc-danger)  !important; color: var(--nc-danger)  !important; }
.btn-outline-danger:hover  { background: rgba(239, 68, 68,.1) !important; }
.btn-outline-warning { border-color: var(--nc-warning) !important; color: var(--nc-warning) !important; }

.btn-link { color: var(--nc-primary) !important; }

/* ─── Badges ─────────────────────────────────────────── */
.badge {
  font-size: 10px !important;
  font-weight: 600 !important;
  padding: 3px 8px !important;
  border-radius: 20px !important;
  letter-spacing: .02em;
}
.badge.bg-primary,
.bg-primary.badge   { background-color: rgba(6, 182, 212, 0.15) !important; color: #22d3ee !important; border: 1px solid rgba(6,182,212,.35); }
.badge.bg-success,
.bg-success.badge   { background-color: rgba(16, 185, 129, 0.15) !important; color: #34d399 !important; border: 1px solid rgba(16,185,129,.35); }
.badge.bg-danger,
.bg-danger.badge    { background-color: rgba(239, 68, 68, 0.15) !important; color: #f87171 !important; border: 1px solid rgba(239,68,68,.35); }
.badge.bg-warning,
.bg-warning.badge   { background-color: rgba(245, 158, 11, 0.15) !important; color: #fbbf24 !important; border: 1px solid rgba(245,158,11,.35); }
.badge.bg-info,
.bg-info.badge      { background-color: rgba(59, 130, 246, 0.15) !important; color: #60a5fa !important; border: 1px solid rgba(59,130,246,.35); }
.badge.bg-secondary,
.bg-secondary.badge { background-color: rgba(255, 255, 255, 0.08) !important; color: #94a3b8 !important; border: 1px solid rgba(255,255,255,.15); }
.badge.bg-light,
.bg-light.badge     { background-color: rgba(255, 255, 255, 0.06) !important; color: #94a3b8 !important; border: 1px solid rgba(255,255,255,.12); }

/* Non-badge bg-* utilities (alerts, etc) */
.bg-primary:not(.badge)   { background-color: rgba(6, 182, 212, 0.10) !important; color: var(--nc-primary) !important; }
.bg-success:not(.badge)   { background-color: rgba(16,185,129,.10) !important; color: var(--nc-success) !important; }
.bg-danger:not(.badge)    { background-color: rgba(239,68,68,.10)  !important; color: var(--nc-danger)  !important; }
.bg-warning:not(.badge)   { background-color: rgba(245,158,11,.10) !important; color: var(--nc-warning) !important; }
.bg-info:not(.badge)      { background-color: rgba(59,130,246,.10) !important; color: var(--nc-info)    !important; }
.bg-secondary:not(.badge) { background-color: rgba(255,255,255,.06) !important; color: var(--nc-text-muted) !important; }

/* ─── Modals ─────────────────────────────────────────── */
.modal-content {
  background: var(--nc-bg-2) !important;
  border: 1px solid var(--nc-card-border) !important;
  border-radius: var(--nc-radius-lg) !important;
  box-shadow: var(--nc-shadow) !important;
  color: var(--nc-text) !important;
}
.modal-header {
  border-bottom: 1px solid var(--nc-card-border) !important;
  background: rgba(255,255,255,.02) !important;
}
.modal-footer {
  border-top: 1px solid var(--nc-card-border) !important;
  background: rgba(255,255,255,.02) !important;
}
.modal-title { color: var(--nc-text) !important; font-weight: 600; }
.modal-backdrop { backdrop-filter: blur(4px); }
.btn-close {
  filter: invert(1) !important;
  opacity: .5 !important;
}
.btn-close:hover { opacity: 1 !important; }

/* ─── Dropdowns ──────────────────────────────────────── */
.dropdown-menu {
  background: var(--nc-bg-3) !important;
  border: 1px solid var(--nc-card-border) !important;
  border-radius: var(--nc-radius) !important;
  box-shadow: var(--nc-shadow) !important;
  padding: 4px !important;
}
.dropdown-item {
  color: var(--nc-text) !important;
  border-radius: var(--nc-radius-sm) !important;
  font-size: 13px;
  padding: 7px 12px !important;
  transition: background var(--nc-transition);
}
.dropdown-item:hover  { background: rgba(255,255,255,.06) !important; color: var(--nc-text) !important; }
.dropdown-item:focus  { background: var(--nc-primary-subtle) !important; color: var(--nc-primary) !important; }
.dropdown-divider { border-color: var(--nc-card-border) !important; }

/* ─── Nav tabs ───────────────────────────────────────── */
.nav-tabs {
  border-bottom: 1px solid rgba(255,255,255,.07) !important;
  gap: 2px;
}
.nav-tabs .nav-link {
  color: var(--nc-text-muted) !important;
  border: none !important;
  border-radius: var(--nc-radius-sm) var(--nc-radius-sm) 0 0 !important;
  padding: 8px 16px !important;
  font-size: 13px;
  font-weight: 500;
  transition: color var(--nc-transition), background var(--nc-transition);
}
.nav-tabs .nav-link:hover { color: var(--nc-text) !important; background: rgba(255,255,255,.04) !important; }
.nav-tabs .nav-link.active {
  color: var(--nc-primary) !important;
  background: rgba(6,182,212,.08) !important;
  border-bottom: 2px solid var(--nc-primary) !important;
}
.nav-pills .nav-link { color: var(--nc-text-muted) !important; border-radius: var(--nc-radius) !important; }
.nav-pills .nav-link.active { background: var(--nc-primary-subtle) !important; color: var(--nc-primary) !important; }

/* ─── Alerts ─────────────────────────────────────────── */
.alert {
  border-radius: var(--nc-radius) !important;
  border: 1px solid transparent;
  font-size: 13px;
}
.alert-info     { background: rgba(59,130,246,.1) !important; border-color: rgba(59,130,246,.25) !important; color: #93c5fd !important; }
.alert-success  { background: rgba(16,185,129,.1) !important; border-color: rgba(16,185,129,.25) !important; color: #6ee7b7 !important; }
.alert-warning  { background: rgba(245,158,11,.1) !important; border-color: rgba(245,158,11,.25) !important; color: #fcd34d !important; }
.alert-danger   { background: rgba(239,68,68,.1)  !important; border-color: rgba(239,68,68,.25)  !important; color: #fca5a5 !important; }

/* ─── Pagination ─────────────────────────────────────── */
.page-link {
  background: rgba(255,255,255,.04) !important;
  border-color: rgba(255,255,255,.1) !important;
  color: var(--nc-text-muted) !important;
  border-radius: var(--nc-radius-sm) !important;
  font-size: 13px;
  margin: 0 2px;
  transition: all var(--nc-transition);
}
.page-link:hover { background: rgba(255,255,255,.08) !important; color: var(--nc-text) !important; border-color: rgba(255,255,255,.15) !important; }
.page-item.active .page-link {
  background: var(--nc-primary) !important;
  border-color: var(--nc-primary) !important;
  color: #020617 !important;
  box-shadow: 0 0 10px var(--nc-primary-glow);
}
.page-item.disabled .page-link { opacity: .3 !important; }

/* ─── Progress ───────────────────────────────────────── */
.progress {
  background: rgba(255,255,255,.06) !important;
  border-radius: 10px !important;
  height: 5px;
}
.progress-bar { background: var(--nc-primary) !important; }

/* ─── List groups ────────────────────────────────────── */
.list-group-item {
  background: transparent !important;
  border-color: rgba(255,255,255,.07) !important;
  color: var(--nc-text) !important;
}
.list-group-item.active { background: var(--nc-primary-subtle) !important; border-color: var(--nc-primary) !important; color: var(--nc-primary) !important; }
.list-group-item:hover  { background: rgba(255,255,255,.03) !important; }

/* ─── Tooltips & popovers ────────────────────────────── */
.tooltip-inner {
  background: var(--nc-bg-3) !important;
  color: var(--nc-text) !important;
  border: 1px solid var(--nc-card-border);
  border-radius: var(--nc-radius-sm) !important;
  font-size: 12px;
  box-shadow: var(--nc-shadow-sm);
}

/* ─── Spinners ───────────────────────────────────────── */
.spinner-border.text-primary,
.spinner-grow.text-primary { color: var(--nc-primary) !important; }

/* ─── Text utilities ─────────────────────────────────── */
.text-muted    { color: var(--nc-text-muted) !important; }
td.text-muted    { color: var(--nc-text-muted) !important; }
span.text-muted    { color: var(--nc-text-muted) !important; }
button.text-muted    { color: var(--nc-text-muted) !important; }
div.text-muted    { color: var(--nc-text-muted) !important; }
pre.text-muted { color: var(--nc-text-muted) !important; }
small.text-muted { color: var(--nc-text-muted) !important; }
.text-primary  { color: var(--nc-primary) !important; }
.text-success  { color: var(--nc-success) !important; }
.text-danger   { color: var(--nc-danger)  !important; }
.text-warning  { color: var(--nc-warning) !important; }
.text-info     { color: var(--nc-info)    !important; }

/* ─── Borders ────────────────────────────────────────── */
.border          { border-color: var(--nc-card-border) !important; }
.border-top      { border-top-color: var(--nc-card-border) !important; }
.border-bottom   { border-bottom-color: var(--nc-card-border) !important; }
.border-primary  { border-color: var(--nc-primary) !important; }

/* ─── Backgrounds ────────────────────────────────────── */
.bg-dark  { background: var(--nc-bg-2) !important; }
.bg-light { background: rgba(255,255,255,.04) !important; color: var(--nc-text) !important; }
.bg-white { background: rgba(255,255,255,.04) !important; color: var(--nc-text) !important; }
.bg-body-tertiary { background: var(--nc-bg-3) !important; }
.bg-body-secondary { background: var(--nc-bg-2) !important; }

/* ─── Page-level helpers ─────────────────────────────── */
.page-header {
  margin-bottom: var(--nc-pad);
}
.page-header h4,
.page-header h5 {
  font-size: 18px;
  font-weight: 700;
  letter-spacing: -0.4px;
  color: var(--nc-text);
}

/* Status dot animation */
@keyframes nc-blink {
  0%, 100% { box-shadow: 0 0 3px currentColor; }
  50%       { box-shadow: 0 0 8px currentColor, 0 0 12px currentColor; }
}
.status-dot-live { animation: nc-blink 2.2s ease-in-out infinite; }

/* Cyan glow utility */
.nc-glow { box-shadow: 0 0 16px var(--nc-primary-glow); }

/* Table in card with no padding */
.card .table { margin-bottom: 0; }
.card .card-body.p-0 { border-radius: inherit; overflow: hidden; }

/* ─── Bootstrap subtle variants override ─────────────── */
.bg-primary-subtle  { background: var(--nc-primary-subtle) !important; }
.text-primary-emphasis { color: var(--nc-primary) !important; }
.border-primary-subtle { border-color: rgba(6,182,212,.3) !important; }
.bg-success-subtle  { background: rgba(16,185,129,.1) !important; }
.bg-danger-subtle   { background: rgba(239,68,68,.1)  !important; }
.bg-warning-subtle  { background: rgba(245,158,11,.1) !important; }
.bg-info-subtle     { background: rgba(59,130,246,.1) !important; }
.text-dark          { color: #e2e8f0 !important; }
button.text-dark    { color: #e2e8f0 !important; }
.text-secondary     { color: #64748b !important; }
.text-body          { color: var(--nc-text) !important; }
.text-body-secondary { color: var(--nc-text-muted) !important; }
.bg-body            { background: var(--nc-bg) !important; }
.text-bg-info       { background: rgba(6,182,212,.15) !important; color: var(--nc-primary) !important; }
.font-monospace     { font-family: 'Fira Code', 'JetBrains Mono', monospace !important; font-size: .85em; }

/* Code elements */
code {
  color: var(--nc-primary) !important;
  background: rgba(6, 182, 212, 0.08) !important;
  padding: 1px 5px;
  border-radius: 3px;
  font-family: 'Fira Code', 'JetBrains Mono', monospace;
  font-size: .85em;
}
pre code {
  background: transparent !important;
  padding: 0;
}

/* ─── Accordion ──────────────────────────────────────── */
.accordion-item   { background: var(--nc-card) !important; border-color: var(--nc-card-border) !important; }
.accordion-button { background: rgba(255,255,255,.03) !important; color: var(--nc-text) !important; box-shadow: none !important; }
.accordion-button:not(.collapsed) { background: var(--nc-primary-subtle) !important; color: var(--nc-primary) !important; }
.accordion-button::after { filter: invert(1) brightness(.7); }

/* ─── Tab content ────────────────────────────────────── */
.tab-content { padding-top: var(--nc-pad); }

/* ─── Close / X buttons ──────────────────────────────── */
.btn-close { filter: invert(1) opacity(.5) !important; }
.btn-close:hover { filter: invert(1) opacity(1) !important; }

/* ─── Hover on rows ──────────────────────────────────── */
tbody tr { transition: background var(--nc-transition); }

/* ─── Range inputs ───────────────────────────────────── */
input[type=range] { accent-color: var(--nc-primary); }

/* ─── Color input ────────────────────────────────────── */
input[type=color] {
  background: rgba(255,255,255,.04) !important;
  border: 1px solid rgba(255,255,255,.1) !important;
  border-radius: var(--nc-radius) !important;
  padding: 2px 4px;
  height: 36px;
  cursor: pointer;
}

/* ─── HR divider ─────────────────────────────────────── */
hr { border-color: var(--nc-card-border) !important; opacity: 1; }

/* ═══════════════════════════════════════════════════════
   LIGHT THEME OVERRIDES
   Apply via html.theme-light
═══════════════════════════════════════════════════════ */
html.theme-light {
  /* Brand — keep same accent */
  --nc-primary:        #0891b2;
  --nc-primary-dark:   #0e7490;
  --nc-primary-glow:   rgba(8, 145, 178, 0.18);
  --nc-primary-subtle: rgba(8, 145, 178, 0.08);

  /* Surfaces */
  --nc-bg:             #f8fafc;
  --nc-bg-2:           #f1f5f9;
  --nc-bg-3:           #e2e8f0;
  --nc-card:           #ffffff;
  --nc-card-border:    rgba(0, 0, 0, 0.08);
  --nc-card-hover:     #f1f5f9;

  /* Bootstrap overrides */
  --bs-body-bg:              #f8fafc !important;
  --bs-body-color:           #1e293b !important;
  --bs-secondary-bg:         #f1f5f9 !important;
  --bs-tertiary-bg:          #e2e8f0 !important;
  --bs-border-color:         rgba(0,0,0,0.08) !important;
  --bs-border-color-translucent: rgba(0,0,0,0.08) !important;
  --bs-emphasis-color:       #0f172a !important;
  --bs-secondary-color:      #64748b !important;
  --bs-link-color:           #0891b2 !important;
  --bs-link-hover-color:     #0e7490 !important;
  --bs-card-bg:              #ffffff !important;
  --bs-card-border-color:    rgba(0,0,0,0.08) !important;
  --bs-card-cap-bg:          rgba(0,0,0,0.02) !important;
  --bs-table-bg:             transparent !important;
  --bs-table-color:          #fff !important;
  --bs-table-striped-bg:     rgba(0,0,0,0.015) !important;
  --bs-table-hover-bg:       rgba(0,0,0,0.025) !important;
  --bs-table-border-color:   rgba(0,0,0,0.06) !important;
  --bs-input-bg:             #ffffff !important;
  --bs-input-border-color:   rgba(0,0,0,0.12) !important;
  --bs-input-color:          #1e293b !important;
  --bs-input-placeholder-color: #94a3b8 !important;
  --bs-modal-bg:             #ffffff !important;
  --bs-modal-border-color:   rgba(0,0,0,0.08) !important;
  --bs-dropdown-bg:          #ffffff !important;
  --bs-dropdown-border-color: rgba(0,0,0,0.08) !important;
  --bs-dropdown-link-color:  #1e293b !important;
  --bs-dropdown-link-hover-bg: rgba(0,0,0,0.04) !important;
  --bs-pagination-bg:        #ffffff !important;
  --bs-pagination-border-color: rgba(0,0,0,0.1) !important;
  --bs-pagination-color:     #64748b !important;
  --bs-pagination-active-bg: #0891b2 !important;
  --bs-pagination-active-border-color: #0891b2 !important;
  --bs-list-group-bg:        transparent !important;
  --bs-list-group-border-color: rgba(0,0,0,0.06) !important;

  /* Sidebar */
  --nc-sidebar-bg:     linear-gradient(180deg,
                         color-mix(in srgb, #f1f5f9 85%, #0891b2 15%) 0%,
                         #f1f5f9 60%);
  --nc-sidebar-border: rgba(0, 0, 0, 0.06);

  /* Text */
  --nc-text:           #1e293b !important;
  --nc-text-muted:     #64748b !important;
  --nc-text-subtle:    #94a3b8 !important;

  /* Status — slightly darker for light bg */
  --nc-success:        #059669 !important;
  --nc-warning:        #d97706 !important;
  --nc-danger:         #dc2626 !important;
  --nc-info:           #2563eb !important;

  /* Effects */
  --nc-shadow-sm:      0 1px 3px rgba(0,0,0,.08), 0 1px 2px rgba(0,0,0,.04);
  --nc-shadow:         0 4px 12px rgba(0,0,0,.08), 0 1px 3px rgba(0,0,0,.06);
}

/* Light theme — base overrides */
html.theme-light,
html.theme-light body {
  background-color: #f8fafc !important;
  color: #1e293b !important;
}
html.theme-light #app,
html.theme-light .app-container,
html.theme-light .main-content,
html.theme-light .page-content {
  background-color: #f8fafc !important;
  color: #1e293b !important;
}

/* Light — cards */
html.theme-light .card {
  background-color: #ffffff !important;
  border-color: rgba(0,0,0,.08) !important;
  box-shadow: 0 1px 3px rgba(0,0,0,.06) !important;
  color: #1e293b !important;
}
html.theme-light .card:hover { border-color: rgba(8,145,178,.25) !important; }
html.theme-light .card-header {
  background-color: rgba(0,0,0,.015) !important;
  border-bottom-color: rgba(0,0,0,.06) !important;
  color: #1e293b !important;
}
html.theme-light .card-body { color: #1e293b !important; }

/* Light — forms */
html.theme-light .form-control,
html.theme-light .form-select {
  background: #ffffff !important;
  border-color: rgba(0,0,0,.12) !important;
  color: #1e293b !important;
}
html.theme-light .form-control:focus,
html.theme-light .form-select:focus {
  background: #ffffff !important;
  border-color: #0891b2 !important;
  box-shadow: 0 0 0 3px rgba(8,145,178,.15) !important;
}
html.theme-light .form-control::placeholder { color: #94a3b8 !important; }
html.theme-light .form-label { color: #334155 !important; }
html.theme-light .form-check-input { background-color: #ffffff !important; border-color: rgba(0,0,0,.2) !important; }
html.theme-light .form-check-input:checked { background-color: #0891b2 !important; border-color: #0891b2 !important; }
html.theme-light .input-group-text { background: #f1f5f9 !important; border-color: rgba(0,0,0,.12) !important; color: #64748b !important; }
/* Light — select dropdown options */
html.theme-light .form-select option {
  background: #ffffff !important;
  color: #1e293b !important;
}

/* Light — tables */
html.theme-light .table { color: #1e293b !important; }
html.theme-light .table > thead > tr > th { color: #64748b !important; border-bottom-color: rgba(0,0,0,.08) !important; }
html.theme-light .table > tbody > tr > td { border-color: rgba(0,0,0,.04) !important; }
html.theme-light .table-hover > tbody > tr:hover > td { background: rgba(0,0,0,.02) !important; }

/* Light — buttons */
html.theme-light .btn-primary {
  background: #0891b2 !important; border-color: #0891b2 !important; color: #fff !important;
  box-shadow: 0 1px 3px rgba(8,145,178,.25);
}
html.theme-light .btn-primary:hover { background: #0e7490 !important; border-color: #0e7490 !important; }
html.theme-light .btn-secondary { background: #e2e8f0 !important; border-color: #cbd5e1 !important; color: #334155 !important; }
html.theme-light .btn-secondary:hover { background: #cbd5e1 !important; color: #1e293b !important; }
html.theme-light .btn-outline-secondary { border-color: #cbd5e1 !important; color: #64748b !important; }
html.theme-light .btn-outline-secondary:hover { background: #f1f5f9 !important; color: #334155 !important; }

/* Light — badges */
html.theme-light .badge.bg-primary  { background-color: rgba(8,145,178,.1) !important; color: #0891b2 !important; border-color: rgba(8,145,178,.25) !important; }
html.theme-light .badge.bg-success  { background-color: rgba(5,150,105,.1) !important; color: #059669 !important; border-color: rgba(5,150,105,.25) !important; }
html.theme-light .badge.bg-danger   { background-color: rgba(220,38,38,.08) !important; color: #dc2626 !important; border-color: rgba(220,38,38,.2) !important; }
html.theme-light .badge.bg-warning  { background-color: rgba(217,119,6,.08) !important; color: #b45309 !important; border-color: rgba(217,119,6,.2) !important; }
html.theme-light .badge.bg-info     { background-color: rgba(37,99,235,.08) !important; color: #2563eb !important; border-color: rgba(37,99,235,.2) !important; }
html.theme-light .badge.bg-secondary { background-color: rgba(0,0,0,.05) !important; color: #64748b !important; border-color: rgba(0,0,0,.08) !important; }
html.theme-light .badge.bg-light     { background-color: rgba(0,0,0,.04) !important; color: #64748b !important; border-color: rgba(0,0,0,.06) !important; }

/* Light — modals */
html.theme-light .modal-content { background: #ffffff !important; border-color: rgba(0,0,0,.08) !important; color: #1e293b !important; }
html.theme-light .modal-header { border-bottom-color: rgba(0,0,0,.06) !important; background: rgba(0,0,0,.015) !important; }
html.theme-light .modal-footer { border-top-color: rgba(0,0,0,.06) !important; background: rgba(0,0,0,.015) !important; }
html.theme-light .btn-close { filter: none !important; opacity: .4 !important; }
html.theme-light .btn-close:hover { opacity: .8 !important; }

/* Light — dropdowns */
html.theme-light .dropdown-menu { background: #ffffff !important; border-color: rgba(0,0,0,.08) !important; box-shadow: var(--nc-shadow) !important; }
html.theme-light .dropdown-item { color: #334155 !important; }
html.theme-light .dropdown-item:hover { background: #f1f5f9 !important; }

/* Light — nav tabs */
html.theme-light .nav-tabs { border-bottom-color: rgba(0,0,0,.06) !important; }
html.theme-light .nav-tabs .nav-link { color: #64748b !important; }
html.theme-light .nav-tabs .nav-link:hover { color: #334155 !important; background: rgba(0,0,0,.02) !important; }
html.theme-light .nav-tabs .nav-link.active { color: #0891b2 !important; background: rgba(8,145,178,.06) !important; border-bottom: 2px solid #0891b2 !important; }

/* Light — alerts */
html.theme-light .alert-info    { background: rgba(37,99,235,.06) !important; border-color: rgba(37,99,235,.15) !important; color: #1e40af !important; }
html.theme-light .alert-success { background: rgba(5,150,105,.06) !important; border-color: rgba(5,150,105,.15) !important; color: #065f46 !important; }
html.theme-light .alert-warning { background: rgba(217,119,6,.06) !important; border-color: rgba(217,119,6,.15) !important; color: #92400e !important; }
html.theme-light .alert-danger  { background: rgba(220,38,38,.06) !important; border-color: rgba(220,38,38,.15) !important; color: #991b1b !important; }

/* Light — pagination */
html.theme-light .page-link { background: #ffffff !important; border-color: rgba(0,0,0,.08) !important; color: #64748b !important; }
html.theme-light .page-link:hover { background: #f1f5f9 !important; color: #334155 !important; }
html.theme-light .page-item.active .page-link { background: #0891b2 !important; border-color: #0891b2 !important; color: #fff !important; }

/* Light — text utilities */
html.theme-light .text-muted   { color: #64748b !important; }
html.theme-light .text-primary { color: #0891b2 !important; }
html.theme-light .text-dark    { color: #1e293b !important; }
html.theme-light .text-body    { color: #1e293b !important; }

/* Light — misc */
html.theme-light .bg-light { background: #f1f5f9 !important; color: #334155 !important; }
html.theme-light .bg-white { background: #ffffff !important; color: #1e293b !important; }
html.theme-light .bg-dark  { background: #e2e8f0 !important; color: #1e293b !important; }
html.theme-light .bg-body-tertiary { background: #f8fafc !important; }
html.theme-light .bg-body-secondary { background: #f1f5f9 !important; }
html.theme-light .border   { border-color: rgba(0,0,0,.08) !important; }
html.theme-light .text-secondary { color: #64748b !important; }

html.theme-light code {
  color: #0e7490 !important;
  background: rgba(8,145,178,.06) !important;
}

html.theme-light .accordion-button { background: rgba(0,0,0,.02) !important; color: #334155 !important; }
html.theme-light .accordion-button:not(.collapsed) { background: rgba(8,145,178,.06) !important; color: #0891b2 !important; }
html.theme-light .accordion-button::after { filter: none; }
html.theme-light .accordion-item { background: #fff !important; border-color: rgba(0,0,0,.06) !important; }

html.theme-light input[type=color] { background: #fff !important; border-color: rgba(0,0,0,.12) !important; }

/* Light — scrollbar */
html.theme-light ::-webkit-scrollbar-thumb { background: rgba(0,0,0,.12); }
html.theme-light ::-webkit-scrollbar-thumb:hover { background: rgba(0,0,0,.22); }

/* Light — mobile header */
html.theme-light .app-header {
  background: rgba(248,250,252,.9);
  border-bottom-color: rgba(0,0,0,.06);
}
html.theme-light .header-title { color: #1e293b; }

/* Light — spinners */
html.theme-light .spinner-border.text-primary { color: #0891b2 !important; }

/* Light — list-group */
html.theme-light .list-group-item { color: #1e293b !important; border-color: rgba(0,0,0,.06) !important; }
html.theme-light .list-group-item.active { background: rgba(8,145,178,.06) !important; color: #0891b2 !important; border-color: #0891b2 !important; }
html.theme-light .list-group-item:hover { background: rgba(0,0,0,.02) !important; }

/* Light — sidebar (scoped styles need deep overrides) */
html.theme-light .sidebar {
  background: linear-gradient(180deg,
    color-mix(in srgb, #f1f5f9 88%, #0891b2 12%) 0%,
    #f1f5f9 60%) !important;
  border-right-color: rgba(0,0,0,.06) !important;
}
html.theme-light .sidebar-brand { border-bottom-color: rgba(0,0,0,.06) !important; }
html.theme-light .brand-text { color: #1e293b !important; }
html.theme-light .brand-ver  { color: #94a3b8 !important; }
html.theme-light .collapse-btn {
  background: rgba(0,0,0,.04) !important;
  border-color: rgba(0,0,0,.08) !important;
  color: #64748b !important;
}
html.theme-light .collapse-btn:hover { background: rgba(0,0,0,.08) !important; color: #0891b2 !important; }
html.theme-light .nav-item,
html.theme-light .nav-group-header { color: #64748b !important; }
html.theme-light .nav-item:hover,
html.theme-light .nav-group-header:hover { background: rgba(0,0,0,.04) !important; color: #334155 !important; }
html.theme-light .nav-item.active {
  background: rgba(8,145,178,.08) !important;
  color: #0891b2 !important;
}
html.theme-light .nav-subitem { color: #64748b !important; }
html.theme-light .nav-subitem:hover { background: rgba(0,0,0,.03) !important; color: #334155 !important; }
html.theme-light .nav-subitem.active { color: #0891b2 !important; background: rgba(8,145,178,.06) !important; }
html.theme-light .nav-subitem.active .sub-dot { background: #0891b2 !important; box-shadow: 0 0 4px #0891b2 !important; }
html.theme-light .sub-dot { background: #cbd5e1 !important; }
html.theme-light .nav-chevron { color: #94a3b8 !important; }
html.theme-light .nav-group-header.group-active { color: #334155 !important; }
html.theme-light .sidebar-footer { border-top-color: rgba(0,0,0,.06) !important; }
html.theme-light .user-avatar {
  background: linear-gradient(135deg, #0891b2 0%, #0e7490 100%) !important;
  color: #fff !important;
}
html.theme-light .user-name { color: #334155 !important; }
html.theme-light .user-role { color: #94a3b8 !important; }
html.theme-light .user-row:hover { background: rgba(0,0,0,.03) !important; }
html.theme-light .logout-btn { color: #94a3b8 !important; }
html.theme-light .logout-btn:hover { color: #dc2626 !important; background: rgba(220,38,38,.06) !important; }
html.theme-light .theme-toggle-btn {
  border-color: rgba(0,0,0,.08) !important;
  background: rgba(0,0,0,.03) !important;
  color: #64748b !important;
}
html.theme-light .theme-toggle-btn:hover {
  background: rgba(8,145,178,.08) !important;
  border-color: #0891b2 !important;
  color: #0891b2 !important;
}
html.theme-light .theme-label { color: #64748b !important; }

/* Light — Dashboard */
html.theme-light .bento-card {
  background: rgba(255,255,255,.95) !important;
  border-color: rgba(0,0,0,.06) !important;
  box-shadow: 0 1px 3px rgba(0,0,0,.06) !important;
}
html.theme-light .bento-card:hover {
  border-color: rgba(8,145,178,.2) !important;
  box-shadow: 0 4px 12px rgba(0,0,0,.08) !important;
}
html.theme-light .dash-eyebrow { color: #0891b2 !important; }
html.theme-light .dash-title { color: #0f172a !important; }
html.theme-light .stat-value { color: #0f172a !important; }
html.theme-light .dash-refresh {
  border-color: rgba(0,0,0,.1) !important;
  background: rgba(0,0,0,.02) !important;
  color: #64748b !important;
}
html.theme-light .dash-alert--danger  { background: rgba(220,38,38,.06) !important; border-color: rgba(220,38,38,.2) !important; color: #991b1b !important; }
html.theme-light .dash-alert--warning { background: rgba(217,119,6,.06) !important; border-color: rgba(217,119,6,.2) !important; color: #92400e !important; }

/* Light — Sessions status badges */
html.theme-light .status-badge--active { background: rgba(5,150,105,.1) !important; color: #059669 !important; border-color: rgba(5,150,105,.25) !important; }
html.theme-light .status-badge--stopped { background: rgba(37,99,235,.08) !important; color: #2563eb !important; border-color: rgba(37,99,235,.2) !important; }
html.theme-light .status-badge--paused { background: rgba(217,119,6,.08) !important; color: #b45309 !important; border-color: rgba(217,119,6,.2) !important; }
html.theme-light .status-badge--pending { background: rgba(0,0,0,.04) !important; color: #64748b !important; border-color: rgba(0,0,0,.08) !important; }
html.theme-light .status-badge--awaiting_code,
html.theme-light .status-badge--awaiting_2fa { background: rgba(217,119,6,.08) !important; color: #b45309 !important; border-color: rgba(217,119,6,.2) !important; }
html.theme-light .status-badge--inactive { background: rgba(220,38,38,.06) !important; color: #dc2626 !important; border-color: rgba(220,38,38,.2) !important; }
html.theme-light .status-dot--active { background: #059669 !important; box-shadow: 0 0 4px #059669 !important; }
html.theme-light .status-dot--warn { background: #d97706 !important; box-shadow: 0 0 4px #d97706 !important; }
</style>
